import React from 'react';
import {
  List,
  Datagrid,
  TextField,
  FunctionField,
  EditButton,
  useTranslate,
  Filter,
  TopToolbar,
  ExportButton,
  RefreshButton,
  SelectInput,
  TextInput,
  BooleanInput,
  BooleanField,
  useListContext,
  RecordContextProvider,
} from 'react-admin';
import {
  Chip,
  Box,
  Typography,
  Tooltip,
  Switch,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  CreditCard,
  AccountBalanceWallet,
  PhoneIphone,
  CheckCircle,
  Cancel,
  DragIndicator,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const PaymentMethodsFilter = (props: any) => {
  const translate = useTranslate();

  return (
    <Filter {...props}>
      <TextInput
        label={translate('resources.payment-methods.fields.search')}
        source="search"
        alwaysOn
      />
      <SelectInput
        source="type"
        label={translate('resources.payment-methods.fields.type')}
        choices={[
          { id: 'card', name: translate('resources.payment-methods.types.card') },
          { id: 'ewallet', name: translate('resources.payment-methods.types.ewallet') },
          { id: 'mobile_wallet', name: translate('resources.payment-methods.types.mobile_wallet') },
        ]}
      />
      <SelectInput
        source="gateway_id"
        label={translate('resources.payment-methods.fields.gateway')}
        choices={[]}
      />
      <BooleanInput
        source="is_active"
        label={translate('resources.payment-methods.fields.is_active')}
      />
    </Filter>
  );
};

const DesktopListActions = () => (
  <TopToolbar>
    <ExportButton />
    <RefreshButton />
  </TopToolbar>
);

const MobileListActions = () => (
  <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5, p: 0 }}>
    <ExportButton />
    <RefreshButton />
  </Box>
);

const TypeIcon = ({ type }: { type: string }) => {
  switch (type) {
    case 'card':
      return <CreditCard fontSize="small" color="primary" />;
    case 'ewallet':
      return <AccountBalanceWallet fontSize="small" color="secondary" />;
    case 'mobile_wallet':
      return <PhoneIphone fontSize="small" color="action" />;
    default:
      return <CreditCard fontSize="small" />;
  }
};

const ActiveChip = ({ isActive }: { isActive: boolean }) => {
  const translate = useTranslate();

  return (
    <Chip
      label={isActive ? translate('common.active') : translate('common.inactive')}
      color={isActive ? 'success' : 'default'}
      size="small"
      icon={isActive ? <CheckCircle fontSize="small" /> : <Cancel fontSize="small" />}
    />
  );
};

const DeviceTypeChip = ({ deviceType }: { deviceType: string }) => {
  const translate = useTranslate();

  const getLabel = () => {
    switch (deviceType) {
      case 'ios':
        return 'iOS';
      case 'android':
        return 'Android';
      default:
        return translate('resources.payment-methods.device_types.all');
    }
  };

  return (
    <Chip
      label={getLabel()}
      size="small"
      variant="outlined"
      color={deviceType === 'all' ? 'default' : 'primary'}
    />
  );
};

const MobileList = () => {
  const { data, isLoading } = useListContext();
  const navigate = useNavigate();
  const translate = useTranslate();
  if (isLoading || !data) return null;
  return (
    <Box>
      {data.map((record: any) => (
        <RecordContextProvider key={record.id} value={record}>
          <Box
            onClick={() => navigate(`/payment-methods/${record.id}`)}
            sx={{ py: 1.5, px: 2, borderBottom: '1px solid #F0F0F0', cursor: 'pointer' }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 0.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}>
                <TypeIcon type={record.type} />
                <Typography sx={{ fontWeight: 600, fontSize: '0.875rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {record.name}
                </Typography>
              </Box>
              <Box sx={{ flexShrink: 0 }}>
                <ActiveChip isActive={record.is_active} />
              </Box>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Typography variant="caption" color="text.secondary">
                {record.code}
              </Typography>
              {record.gateway?.name && (
                <Chip label={record.gateway.name} size="small" variant="outlined" />
              )}
              <DeviceTypeChip deviceType={record.device_type} />
            </Box>
          </Box>
        </RecordContextProvider>
      ))}
    </Box>
  );
};

export const PaymentMethodsList = () => {
  const translate = useTranslate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  return (
    <List
      filters={<PaymentMethodsFilter />}
      actions={isMobile ? <MobileListActions /> : <DesktopListActions />}
      perPage={25}
      sort={{ field: 'sort_order', order: 'ASC' }}
      empty={isMobile ? false : undefined}
      sx={{
        '& .RaList-content': {
          backgroundColor: isMobile ? 'transparent' : '#FAFAFA',
        },
        ...(isMobile && {
          '& .RaList-main': { margin: 0 },
          '& .RaList-content': { backgroundColor: 'transparent !important', boxShadow: 'none !important', mt: 2 },
          '& .RaList-actions': { backgroundColor: 'transparent !important', p: '0 !important', minHeight: 'auto !important' },
          '& .RaListToolbar-root': { backgroundColor: 'transparent !important', flexDirection: 'row !important', flexWrap: 'wrap !important', alignItems: 'center !important', gap: '8px !important' },
          '& .RaListToolbar-root > span': { display: 'none' },
          '& .RaFilterButton-root, & .add-filter': { display: 'none !important' },
          '& .RaListToolbar-root > form, & .RaFilterForm-root': { flex: '1 1 100% !important', width: '100% !important' },
          '& .filter-field, & .RaFilterForm-filterFormInput': { width: '100% !important' },
          '& .filter-field .MuiFormControl-root': { width: '100% !important' },
          '& .filter-field .MuiInputBase-root': { width: '100% !important' },
          '& .MuiToolbar-root': { backgroundColor: 'transparent !important', minHeight: 'auto !important' },
        }),
      }}
    >
      {isMobile ? (
        <MobileList />
      ) : (
        <Datagrid
          bulkActionButtons={false}
          rowClick="edit"
          sx={{
            '& .RaDatagrid-headerCell': {
              backgroundColor: '#F5F5F5',
              fontWeight: 'bold',
            },
            '& .RaDatagrid-row': {
              '&:hover': {
                backgroundColor: '#F0F7FF',
              },
            },
            '& .column-sort_order': { width: '60px' },
            '& .column-type': { width: '100px' },
            '& .column-name': { minWidth: '150px', flex: 1 },
            '& .column-gateway': { width: '120px' },
            '& .column-device_type': { width: '100px' },
            '& .column-tokenization': { width: '120px' },
            '& .column-is_active': { width: '100px' },
            '& .column-actions': { width: '80px' },
          }}
        >
          <FunctionField
            label=""
            render={() => (
              <Tooltip title={translate('resources.payment-methods.hints.drag_to_reorder')}>
                <DragIndicator color="disabled" sx={{ cursor: 'grab' }} />
              </Tooltip>
            )}
            source="sort_order"
          />

          <FunctionField
            label={translate('resources.payment-methods.fields.type')}
            render={(record: any) => (
              <Box display="flex" alignItems="center" gap={1}>
                <TypeIcon type={record.type} />
                <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                  {translate(`resources.payment-methods.types.${record.type}`)}
                </Typography>
              </Box>
            )}
            source="type"
          />

          <FunctionField
            label={translate('resources.payment-methods.fields.name')}
            render={(record: any) => (
              <Box>
                <Typography variant="body2" fontWeight={500}>
                  {record.name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {record.code}
                </Typography>
              </Box>
            )}
            source="name"
          />

          <FunctionField
            label={translate('resources.payment-methods.fields.gateway')}
            render={(record: any) => (
              <Chip
                label={record.gateway?.name || record.gateway?.code || '-'}
                size="small"
                variant="outlined"
              />
            )}
            source="gateway"
          />

          <FunctionField
            label={translate('resources.payment-methods.fields.device_type')}
            render={(record: any) => (
              <DeviceTypeChip deviceType={record.device_type} />
            )}
            source="device_type"
          />

          <FunctionField
            label={translate('resources.payment-methods.fields.supports_tokenization')}
            render={(record: any) => (
              record.supports_tokenization ? (
                <Chip
                  label={translate('common.yes')}
                  size="small"
                  color="info"
                />
              ) : (
                <Typography variant="body2" color="text.secondary">-</Typography>
              )
            )}
            source="tokenization"
          />

          <FunctionField
            label={translate('resources.payment-methods.fields.is_active')}
            render={(record: any) => (
              <ActiveChip isActive={record.is_active} />
            )}
            source="is_active"
          />

          <FunctionField
            label=""
            render={(record: any) => (
              <Box display="flex" gap={0}>
                <Tooltip title={translate('ra.action.edit')}>
                  <span>
                    <EditButton record={record} size="small" label="" />
                  </span>
                </Tooltip>
              </Box>
            )}
            source="actions"
            textAlign="right"
          />
        </Datagrid>
      )}
    </List>
  );
};
